import {useInjection} from "inversify-react";
import {useCallback, useEffect, useState} from "react";
import ApiService from "../../services/apiService";
import {OrderApi, OrderCreateDto, OrderDto, OrderRefundApi, OrderUpdateDto} from "../../api";
import OrdersList from "./orderList";
import OrderModalFormAddContainer from "./orderModalFormAddContainer/orderModalFormAddContainer";
import {Button, Layout, message, Modal} from "antd";
import {Content} from "antd/lib/layout/layout";
import styles from "./orderListContainer.module.scss";
import {AsyncButton} from "digimuth-components/dist";
import {generatePath, useNavigate} from "react-router-dom";
import {RoutingPaths} from "../routing/routingContainer";

export interface OrderDeleteInfo {
    orderId: number,
    orderNumber: string,
    orderGuid: string,
    refundValue?: number,
    showRefund: boolean
}

export default function OrdersListContainer() {
    const apiService = useInjection(ApiService);
    const navigate = useNavigate();

    const [orders, setOrders] = useState<OrderDto[] | null>();
    const [inCompletedOrders, setInCompletedOrders] = useState<OrderDto[] | null>();
    const [isAddFormVisible, setAddFormVisible] = useState(false);
    const [orderModalFormContainerTitle, setOrderModalFormContainerTitle] = useState<string>();
    const [orderToEdit, setOrderToEdit] = useState<OrderUpdateDto | null>(null);
    const [idOrderToEdit, setIdOrderToEdit] = useState<number | null>(null);
    const [refundModalContent, setRefundModalContent] = useState<OrderDeleteInfo | null>(null);
    const [showRefundModal, setShowRefundModal] = useState<boolean>(false);

    useEffect(() => {
        refreshOrdersList();
    }, [])

    const refreshOrdersList = async () => {
        try {
            const orders = await apiService.getApi(OrderApi)
                .apiOrderCompletedOrdersGet();
            setOrders(orders.data);

            const inCompletedOrders = await apiService.getApi(OrderApi)
                .apiOrderInCompletedOrdersGet();
            setInCompletedOrders(inCompletedOrders.data);

        } catch (e: any) {
            console.log(e.response.data);
        }
    }

    const onAddClicked = () => {
        setOrderModalFormContainerTitle("Dodaj nowe zamówienie");
        setOrderToEdit(null);
        setIdOrderToEdit(null);
        setAddFormVisible(true);
    }

    const onEditClicked = async (id: number) => {
        setOrderModalFormContainerTitle(`Edytuj zamówienie: ${orders?.find(x => x.id === id)?.orderNumber}`);

        const response = await apiService.getApi(OrderApi).apiOrderIdGet(id);

        setOrderToEdit(response.data as OrderUpdateDto);
        setIdOrderToEdit(id);
        setAddFormVisible(true);
    }

    const onDeleteConfirmClicked = useCallback(async (refund: boolean, value?: OrderDeleteInfo) => {
        if (!refundModalContent && !value) return;

        if (refundModalContent && refund && refundModalContent.showRefund && (refundModalContent?.refundValue ?? 0) > 0) {
            try {
                await apiService.getApi(OrderRefundApi).apiOrderRefundPost({
                    //backend will make a refund for exactly 100% of orderValue
                    amount: null,
                    orderGuid: refundModalContent.orderGuid
                });
                message.success(`Pomyślnie dokonano zwrotu środków dla zamówienia ${refundModalContent?.orderNumber}`);
            } catch (e: any) {
                message.error(e.response?.data ?? "Wystąpił błąd podczas próby dokonania zwrotu środków za zamówienie");
                return e.response.data;
            } finally {
                setShowRefundModal(false);
            }
        }

        try {
            await apiService.getApi(OrderApi).apiOrderIdDelete(refundModalContent?.orderId ?? value?.orderId!);
            message.success(`Pomyślnie zaktualizowano stan zamówienia ${refundModalContent?.orderNumber ?? value?.orderNumber}`);
        } catch (e: any) {
            message.error("Wystąpił błąd podczas usuwania zamówienia");
            return e.response?.data;
        } finally {
            await refreshOrdersList();
        }
    }, [refundModalContent]);

    const onSubmit = async (values: OrderCreateDto | OrderUpdateDto) => {
        try {
            if (orderToEdit === null) {
                if (values.deliveryAddressSameAsBillingAddress === undefined) {
                    values.deliveryAddressSameAsBillingAddress = true;
                }
                if (values.deliveryAddressSameAsBillingAddress) {
                    values.billingAddressId = values.deliveryAddressId;
                }
                const response = await apiService.getApi(OrderApi).apiOrderPost(values);
                navigate(generatePath(RoutingPaths.orderDetails.route, {
                        orderId: response.data.id?.toString()
                    })
                );
            } else if (idOrderToEdit !== null) {
                await apiService.getApi(OrderApi).apiOrderIdPut(idOrderToEdit, values)
            }
        } catch (e: any) {
            message.error(e.response?.data);
            return e.response.data;
        }

        setAddFormVisible(false)
        await refreshOrdersList();
    }

    return <Layout style={{backgroundColor: "white"}}>
        <Content>
            <Layout>
                <Content className={styles.content}>
                    <div className={styles.headerContainer}>
                        <h1 className={styles.title}>Zamówienia</h1>
                        <Button type={"primary"} className={styles.addButton} onClick={onAddClicked}>
                            + Dodaj nowe zamówienie
                        </Button>
                    </div>
                    <OrdersList
                        data={orders || null}
                        onEditClicked={onEditClicked}
                        onDelete={async (value) => {
                            setRefundModalContent(value);
                            if (value.showRefund) setShowRefundModal(true);
                            else await onDeleteConfirmClicked(false, value);
                        }}
                    />
                    <h1 style={{marginTop: 50, marginBottom: 20}}>Niedokończone zamówienia</h1>
                    <OrdersList
                        data={inCompletedOrders || null}
                        onDelete={async (value) => {
                            setRefundModalContent(value);
                            if (value.showRefund) setShowRefundModal(true);
                            else await onDeleteConfirmClicked(false, value);
                        }}
                        onEditClicked={onEditClicked}
                    />
                    <OrderModalFormAddContainer
                        onFinish={() => setAddFormVisible(false)}
                        onSubmit={onSubmit}
                        addFormVisibility={isAddFormVisible}
                        title={orderModalFormContainerTitle || ""}
                        OrderToEdit={orderToEdit}
                    />
                    {refundModalContent && refundModalContent.showRefund && <Modal
                        open={showRefundModal}
                        closeIcon={null}
                        title={`Zwrot za zamówienie ${refundModalContent.orderNumber}`}
                        footer={<div className={styles.modalFooter}>
                            <AsyncButton
                                onClick={async () => onDeleteConfirmClicked(true)}
                                type={"primary"}
                            >
                                Tak
                            </AsyncButton>
                            <AsyncButton
                                onClick={async () => await onDeleteConfirmClicked(false)}
                                style={{margin: "0 10px"}}
                            >
                                Nie
                            </AsyncButton>
                            <Button onClick={() => {
                                setShowRefundModal(false);
                                message.info(`Zamówienie ${refundModalContent?.orderNumber} nie zostało anulowane`)
                            }}
                            >
                                Anuluj
                            </Button>
                        </div>}
                    >
                        <div style={{textAlign: "center", margin: "20px 0", fontSize: "16px"}}>
                            Czy chcesz dokonać zwrotu <strong>{refundModalContent.refundValue} zł</strong> za to
                            zamówienie?
                        </div>
                    </Modal>}
                </Content>
            </Layout>
        </Content>
    </Layout>;
}
